import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import type { IPublicClientApplication, AuthenticationResult } from '@azure/msal-browser';

const IS_DEV = import.meta.env.DEV;

async function tryHandleRedirect(instance: IPublicClientApplication): Promise<AuthenticationResult | null> {
  try {
    const response = await instance.handleRedirectPromise();
    if (response?.account) instance.setActiveAccount(response.account);
    return response;
  } catch (err) {
    console.warn('[AuthCallback] Error handling redirect promise:', err);
    return null;
  }
}

async function warmTokenCache(instance: IPublicClientApplication, account: import('@azure/msal-browser').AccountInfo): Promise<void> {
  try {
    await instance.acquireTokenSilent({ scopes: ['openid', 'profile', 'email'], account });
  } catch {
    // Token will be acquired lazily via the api-client interceptor
  }
}

export default function AuthCallback() {
  const { instance } = useMsal();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handleCallback = async () => {
      try {
        setLoading(true);

        const redirectResponse = await tryHandleRedirect(instance);
        const accounts = instance.getAllAccounts();

        if (accounts.length === 0) {
          if (IS_DEV) console.log('[AuthCallback] No accounts found, redirecting to signin');
          navigate('/signin', { replace: true });
          return;
        }

        if (!instance.getActiveAccount()) {
          instance.setActiveAccount(accounts[0]);
        }

        if (!redirectResponse) await warmTokenCache(instance, accounts[0]);

        if (IS_DEV) console.log('[AuthCallback] Authenticated, redirecting to dashboard');
        navigate('/dashboard', { replace: true });
      } catch (err) {
        console.error('[AuthCallback] Callback error:', err);
        setError(err instanceof Error ? err.message : 'Authentication failed');
        setTimeout(() => navigate('/signin?error=auth_failed', { replace: true }), 3000);
      } finally {
        setLoading(false);
      }
    };

    handleCallback();
  }, [instance, navigate]);

  return (
    <div style={{ padding: '2rem', textAlign: 'center' }}>
      {error ? (
        <>
          <h2 style={{ color: 'var(--error-red)' }}>Authentication Error</h2>
          <p>{error}</p>
          <p>Redirecting to sign in...</p>
        </>
      ) : (
        <>
          <h2>{loading ? 'Processing authentication...' : 'Authentication complete'}</h2>
          <p>Please wait while we complete your sign in.</p>
        </>
      )}
    </div>
  );
}
